import React from 'react';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import makeStyles from '@mui/styles/makeStyles';
import { ListEmpty } from 'components/shared/List';
import { DealActivityLinkType } from '@trnsact/trnsact-shared-types';
import { CircularProgress, Stack, LinearProgress, Box } from '@mui/material';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

import { SelectOption } from 'global';
import { ActivityType, SmsConversationData } from '../types';
import { AllDealActivity, VoContact } from '../api/types';
import { CUSTOMER_DEAL_ACTIVITIES_QUERY_NAME, useDealActivityMutations } from '../api';
import { CallCard, NoteCard, EmailCard, EventActivityCard } from './cards';
import { MessageCard } from './cards/MessageCard';

interface Props {
  parentId?: string;
  loading: boolean;
  list: AllDealActivity[];
  parentType: DealActivityLinkType;
  voContacts?: Record<string, VoContact>;
  opportunityOptions?: SelectOption[];
  onOpenConversation?: (data: SmsConversationData) => void;
}

export const DealActivityList = ({
  list,
  loading,
  parentId,
  parentType,
  voContacts,
  opportunityOptions,
  onOpenConversation,
}: Props) => {
  const classes = useStyles();

  const { handleDeleteDealActivity } = useDealActivityMutations();
  const { handleOpen: onOpenCallDialog } = useModal(ModalsKeys.CallActivityDialog);
  const { handleOpen: onOpenNoteDialog } = useModal(ModalsKeys.NoteActivityDialog);

  const isListExistAndUpdating = loading && list.length;

  const handleDeleteActivity = (activity: AllDealActivity) => async () => {
    await handleDeleteDealActivity({ id: activity.id }, api => ({
      awaitRefetchQueries: true,
      refetchQueries: [api.dealActivities, CUSTOMER_DEAL_ACTIVITIES_QUERY_NAME],
    }));
  };

  return (
    <RenderWithFallback
      isLoading={loading && !list.length}
      fallBack={
        <Stack height="100%" width="100%" alignItems="center" justifyContent="center">
          <Stack alignItems="center" justifyContent="center">
            <CircularProgress />
          </Stack>
        </Stack>
      }
    >
      <RenderWithCondition condition={!!list.length} fallBack={<ListEmpty />}>
        <Box className={classes.listItems}>
          {isListExistAndUpdating && <LinearProgress className={classes.loading} />}

          {list.map(activity => {
            const activityParentId = activity.parentId ?? parentId;
            if (activity.type === ActivityType.CALL) {
              return (
                <CallCard
                  key={activity.id}
                  entity={activity}
                  onDelete={handleDeleteActivity(activity)}
                  onUpdate={() => onOpenCallDialog({ parentId: activityParentId, entityId: activity.id, parentType })}
                />
              );
            }

            if (activity.type === ActivityType.NOTE) {
              return (
                <NoteCard
                  key={activity.id}
                  entity={activity}
                  onDelete={handleDeleteActivity(activity)}
                  onUpdate={() => onOpenNoteDialog({ parentId: activityParentId, entityId: activity.id, parentType })}
                />
              );
            }

            if (activity.type === ActivityType.EMAIL) {
              return <EmailCard key={activity.id} entity={activity} />;
            }

            if (activity.type === ActivityType.SMS) {
              return (
                <MessageCard
                  key={activity.id}
                  entity={activity}
                  voContacts={voContacts}
                  onOpenConversation={onOpenConversation}
                />
              );
            }

            if (activity.type === ActivityType.EVENT) {
              return (
                <EventActivityCard
                  key={activity.id}
                  entity={activity}
                  opportunityLabel={opportunityOptions?.find(o => o.value === activity.opportunityId)?.label}
                />
              );
            }

            return null;
          })}
        </Box>
      </RenderWithCondition>
    </RenderWithFallback>
  );
};

const useStyles = makeStyles({
  listItems: {
    gap: '0.5rem',
    height: '100%',
    display: 'flex',
    padding: '0.25rem',
    position: 'relative',
    overflow: 'hidden auto',
    flexDirection: 'column',
  },
  loading: {
    top: 0,
    zIndex: 2,
    position: 'absolute',
    width: 'calc(100% - 0.5rem)',
  },
});
